import {ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {DcpPriceRatePlansData} from '@app/model/price/dcp-price-rate-plans-data';
import {DcpPriceServicePlanData} from '@app/model/price/dcp-price-service-plan-data';
import {DcpCurrencyService} from '@app/services/pricing/dcp-currency.service';
import {DcpRatePlanService} from '@app/services/pricing/dcp-rate-plan.service';
import {PriceDisplayService} from '@app/services/pricing/price-display.service';
import {DcpBundleTemplate, DcpPriceDisplayData, DcpPriceSubscriptionLabels} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CurrentProductService} from '@spartacus/storefront';
import {combineLatest} from 'rxjs';
import {DcpProductUtilitiesService} from '../../../services/dcp-product-utilities.service';
import NumberFormat = Intl.NumberFormat;

@UntilDestroy()
@Component({
  selector: 'dcp-plans-and-pricing-display',
  templateUrl: './plans-and-pricing-display.component.html',
  styleUrls: ['./plans-and-pricing-display.component.scss']
})
export class PlansAndPricingDisplayComponent implements OnInit {

  @Input() servicePlan: DcpPriceServicePlanData;
  @Input() showHeadline: boolean = false;
  @Input() ratePlans: DcpPriceRatePlansData[];
  @Input() bundleTemplate: DcpBundleTemplate;

  priceDisplayByCurrency: DcpPriceSubscriptionLabels;
  currentCurrency: string;
  isSapProduct: boolean;

  private internalPriceDisplay: DcpPriceDisplayData;

  constructor(
    private priceDisplayService: PriceDisplayService,
    private currencyService: DcpCurrencyService,
    private currentProductService: CurrentProductService,
    private ratePlanService: DcpRatePlanService,
    private changeDetectorRef: ChangeDetectorRef,
    private dcpProductUtilitiesService: DcpProductUtilitiesService
  ) {
  }

  get priceDisplay(): DcpPriceDisplayData {
    return this.internalPriceDisplay;
  }

  @Input() set priceDisplay(priceDisplay: DcpPriceDisplayData) {
    this.internalPriceDisplay = priceDisplay;
    this.setPriceDisplayByCurrency(priceDisplay);
  }

  ngOnInit(): void {
    combineLatest([
      this.currencyService.getActive(),
      this.currencyService.getCurrencies()
    ]).pipe(untilDestroyed(this)).subscribe(([defaultCurrency, currencies]) => {
      this.currentCurrency = this.ratePlanService.selectRatePlanCurrency(this.ratePlans, defaultCurrency, currencies);
      this.setPriceDisplayByCurrency(this.internalPriceDisplay);
      this.changeDetectorRef.detectChanges();
    });
    this.currentProductService.getProduct()
      .pipe(untilDestroyed(this))
      .subscribe(product => {
        this.isSapProduct = this.dcpProductUtilitiesService.isSapProduct(product);
        this.changeDetectorRef.detectChanges();
      });
  }

  public cutPriceVisually(price: string) {
    if (price && price.length) {
      const numberFormat: NumberFormat = new NumberFormat('en', {
        style: 'decimal',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      });
      if (price.lastIndexOf('.') >= 0) {
        let commaSeparatedPrice: string = price.slice(0, price.lastIndexOf('.'));
        const priceAsNumber: number = this.parseWholeStringToNumber(commaSeparatedPrice);
        if (priceAsNumber) {
          commaSeparatedPrice = numberFormat.format(parseInt(commaSeparatedPrice, 10)).toString();
        }
        return [
          commaSeparatedPrice,
          price.slice(price.lastIndexOf('.'), price.length)
        ];
      }
      return [price];
    }
    return undefined;
  }

  tooltipVisible(): boolean {
    return !!this.currencyTooltipVisible() || !!this.mainTooltipVisible();
  }

  currencyTooltipVisible(): boolean {
    return !!this.priceDisplayByCurrency?.labels?.tooltip;
  }

  mainTooltipVisible(): boolean {
    return !!this.priceDisplay?.labels?.tooltip;
  }

  private parseWholeStringToNumber(x: string): number {
    const intValue: number = Number(x);
    return intValue === Math.floor(intValue) ? intValue : NaN;
  }

  private setPriceDisplayByCurrency(priceDisplay: DcpPriceDisplayData): void {
    this.priceDisplayByCurrency = this.priceDisplayService.selectSubscriptionLabelByCurrency(priceDisplay, this.currentCurrency);
  }
}
